import { mountHeader } from './components/Header.js'
import { mountModeBody } from './components/ModeBody.js'
import { trackEvent } from './analytics/plausible.js'
import { generateProjectCode } from './codegen/index.js'
import { initSandbox, destroySandbox, mountSandboxIframe } from './sandbox/host.js'
import { STRINGS } from '@tm/shared'
import { showToast } from '@tm/ui-kit'
import {
  saveProject,
  loadProject,
  listProjects,
  setSetting,
  getSetting,
} from './store/ProjectRepository.js'
import { createAutoSave } from './store/AutoSave.js'
import { mountTemplateSelector, loadManifest, loadTemplate } from './ui/TemplateSelector.js'
import { showConfirm } from './ui/ConfirmDialog.js'
import { mountShareModal } from './share/ShareModal.js'
import { estimateUrlSize, isUrlTooLong } from './share/encode.js'
import { mountHintCard } from './onboarding/HintCard.js'
import { createIdleHint } from './onboarding/IdleHint.js'
import { createOnboardingTour } from './onboarding/OnboardingTour.js'
import { mountParentGuide } from './onboarding/ParentGuide.js'
import './styles/onboarding.css'

export function mountApp(rootEl) {
  let activeMode = 'build'
  let workspace = null
  let store = null
  let currentProject = null
  let templateSelectorOpen = false
  let autoSave = null
  let renderSaveStatus = null
  let hintCard = null
  let idleHint = null
  let parentGuide = null
  let onboardingTour = null

  rootEl.innerHTML = `
    <div id="tm-header-host"></div>
    <main id="tm-body-host" class="tm-body">
      <div id="tm-hint-card-host" class="tm-hint-host" aria-live="polite"></div>
    </main>
    <div id="tm-parent-guide-host"></div>
    <div id="tm-onboarding-overlay-host"></div>
  `

  const headerHost = rootEl.querySelector('#tm-header-host')
  const bodyHost = rootEl.querySelector('#tm-body-host')
  const hintCardHost = rootEl.querySelector('#tm-hint-card-host')
  const parentGuideHost = rootEl.querySelector('#tm-parent-guide-host')
  const onboardingHost = rootEl.querySelector('#tm-onboarding-overlay-host')

  // Default hints (fallback jika template tidak punya hints).
  const DEFAULT_HINTS = [
    {
      id: 'default-1',
      icon: '🦘',
      title: 'Coba tambah blok',
      body: 'Tap kategori Motion di atas, lalu tap blok untuk menambah.',
    },
    {
      id: 'default-2',
      icon: '▶',
      title: 'Tekan Mainkan',
      body: 'Lihat hasil game kamu setelah edit blok.',
    },
  ]

  async function checkFirstRun() {
    const projects = await listProjects()
    return projects.length === 0
  }

  async function restoreLastProject() {
    const lastId = await getSetting('lastProjectId')
    if (!lastId) return null
    return loadProject(lastId)
  }

  async function showTemplatePicker() {
    if (templateSelectorOpen) return
    templateSelectorOpen = true
    const selector = await mountTemplateSelector({
      onSelectTemplate: async (tpl) => {
        const project = {
          id: crypto.randomUUID?.() || `p-${Date.now()}-${Math.random().toString(36).slice(2)}`,
          name: tpl.title,
          templateId: tpl.id,
          createdAt: Date.now(),
          updatedAt: Date.now(),
          blocks: JSON.stringify(tpl.presetProgram.blocks),
          initialSprites: tpl.initialSprites,
          canvas: tpl.canvas,
          availableBlocks: tpl.availableBlocks,
        }
        currentProject = project
        store?.loadFromProject(project)
        selector.remove()
        templateSelectorOpen = false
        trackEvent('template_selected', { templateId: tpl.id })
        // Fix Bug C: onboarding baru mulai setelah user memilih template.
        startOnboardingTour()
      },
      onSelectProject: async (proj) => {
        currentProject = proj
        store?.loadFromProject(proj)
        selector.remove()
        templateSelectorOpen = false
        trackEvent('project_loaded', { projectId: proj.id })
        // Fix Bug C: onboarding baru mulai setelah user memilih project.
        startOnboardingTour()
      },
    })
    document.body.appendChild(selector)
  }

  // PersistFn: shared by manual save + auto-save. Wraps IDB write + lastProjectId.
  async function persistProject(project) {
    await saveProject(project)
    await setSetting('lastProjectId', project.id)
    currentProject = project
    trackEvent('project_saved', {
      templateId: project.templateId,
      source: autoSave ? 'auto' : 'manual',
    })
  }

  async function handleSave() {
    if (!store) return
    if (autoSave) {
      // Route via autoSave so the status indicator stays consistent.
      await autoSave.save()
      return
    }
    // Fallback kalau autoSave belum ready.
    try {
      const project = store.getProject()
      await persistProject(project)
      showToast(STRINGS.toastSaved, { type: 'success', durationMs: 1500 })
    } catch (err) {
      showToast(STRINGS.toastSaveError.replace('%s', err.message), {
        type: 'error',
        durationMs: 3000,
      })
    }
  }

  async function handleNewProject() {
    if (!store) return
    const confirmed = await showConfirm({
      title: STRINGS.confirmNewProjectTitle,
      message: STRINGS.confirmNewProjectMessage,
      confirmLabel: STRINGS.confirmNewProjectConfirm,
      danger: true,
    })
    if (!confirmed) return
    store.resetProject()
    currentProject = null
    showTemplatePicker()
  }

  // Privacy-first: confirm opt-in BEFORE generating share URL.
  // Share link = public, siapa pun yang punya URL bisa decode dan main.
  async function handleShare() {
    // Always use the latest workspace state; currentProject may be stale after edits.
    const project = store?.getProject?.() ?? currentProject
    if (!project) {
      showToast(STRINGS.shareEmptyBody, { type: 'warn', durationMs: 2000 })
      return
    }
    if (isUrlTooLong(project)) {
      showToast(STRINGS.shareUrlTooLong, { type: 'warn', durationMs: 3000 })
      trackEvent('share_url_too_long', { projectId: project.id })
      return
    }
    const confirmed = await showConfirm({
      title: STRINGS.shareConfirmTitle,
      message: STRINGS.shareConfirmOptIn,
      confirmLabel: STRINGS.shareConfirmYes,
      cancelLabel: STRINGS.shareConfirmNo,
    })
    if (!confirmed) return
    trackEvent('share_modal_opened', { projectId: project.id })
    const { url } = await mountShareModal({ project })
    if (url) {
      trackEvent('share_link_generated', {
        projectId: project.id,
        projectSizeBytes: estimateUrlSize(project),
      })
    }
  }

  function setupAutoSave() {
    if (autoSave) return
    autoSave = createAutoSave({
      store,
      saveFn: persistProject,
      onStateChange: ({ state }) => renderSaveStatus?.(state),
    })
  }

  // Lazy-load HintCard dengan hints dari template JSON (fallback ke DEFAULT_HINTS).
  async function mountHintCardForTemplate(templateId) {
    if (!templateId || hintCard) return
    const host = hintCardHost
    if (!host) return
    let hints = DEFAULT_HINTS
    try {
      const manifest = await loadManifest()
      const entry = manifest.templates?.find((t) => t.id === templateId)
      const template = entry ? await loadTemplate(entry) : null
      if (template?.hints?.length) {
        hints = template.hints.map((h, i) =>
          typeof h === 'string'
            ? {
                id: `${templateId}-hint-${i}`,
                icon: '💡',
                title: template.title || 'Petunjuk',
                body: h,
              }
            : h,
        )
      }
    } catch {
      // ignore → pakai default
    }
    hintCard = mountHintCard({ host, hints })
  }

  function setupIdleHint() {
    if (idleHint) return
    idleHint = createIdleHint({
      idleMs: 30000,
      suggestions: [
        { icon: '🦊', title: 'Kiko ngantuk', body: 'Coba tambah blok yuk!' },
        { icon: '🎮', title: 'Mau main?', body: 'Tekan ▶ Mainkan untuk lihat hasilnya.' },
        { icon: '🎨', title: 'Ganti sprite', body: 'Buka menu Aset untuk pilih sprite lain.' },
      ],
      onSuggest: (s) =>
        showToast(`${s.icon} ${s.title}: ${s.body}`, { type: 'info', durationMs: 4000 }),
    })
    idleHint.start()
  }

  function setupParentGuide() {
    if (parentGuide) return
    if (!parentGuideHost) return
    parentGuide = mountParentGuide({ host: parentGuideHost, onClose: () => {} })
  }

  function renderOnboardingOverlay() {
    if (!onboardingHost || !onboardingTour) return
    const step = onboardingTour.getCurrentStep()
    onboardingHost.innerHTML = `
        <div class="tm-onboarding-overlay">
          <div class="tm-onboarding-overlay__panel">
            <h3>${escapeHtml(step.title)}</h3>
            <p>${escapeHtml(step.body)}</p>
            <div class="tm-onboarding-overlay__actions">
              <button type="button" data-action="skip">Lewati</button>
              <button type="button" data-action="next">${escapeHtml(step.cta)}</button>
            </div>
            <div class="tm-onboarding-overlay__progress">Sesi ${step.sesi} / 3</div>
          </div>
        </div>
      `
    onboardingHost.querySelector('[data-action="next"]').addEventListener('click', async () => {
      await onboardingTour.next()
      if (onboardingTour.isCompleted()) {
        onboardingHost.innerHTML = ''
      } else {
        renderOnboardingOverlay()
      }
    })
    onboardingHost.querySelector('[data-action="skip"]').addEventListener('click', async () => {
      await onboardingTour.skip()
      onboardingHost.innerHTML = ''
    })
  }

  async function startOnboardingTour() {
    if (onboardingTour) return
    onboardingTour = createOnboardingTour({
      store: {
        getSetting: async (k) => getSetting(k),
        setSetting: async (k, v) => setSetting(k, v),
      },
      onComplete: ({ reason }) => {
        if (reason === 'already-completed') return
        showToast(STRINGS.onboardingSesi1Done, { type: 'success', durationMs: 3000 })
      },
      onSkip: ({ atSesi }) => {
        showToast(`Onboarding dilewati di sesi ${atSesi}`, { type: 'info', durationMs: 2000 })
      },
    })
    await onboardingTour.start()
    if (!onboardingTour.isCompleted()) {
      renderOnboardingOverlay()
    }
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }

  // Keyboard shortcut: Ctrl/Cmd+S = manual save (skip debounce).
  function handleKeydown(e) {
    if ((e.metaKey || e.ctrlKey) && (e.key === 's' || e.key === 'S')) {
      e.preventDefault()
      handleSave()
    }
  }
  document.addEventListener('keydown', handleKeydown)

  function render() {
    const header = mountHeader(headerHost, {
      activeMode,
      onModeChange: (mode) => {
        if (mode === 'play' && store) {
          currentProject = store.getProject()
        }
        if (mode === 'build' && store) {
          currentProject = store.getProject()
        }
        if (mode !== 'play') destroySandbox()
        activeMode = mode
        render()
        if (mode === 'play') handlePlayRequested()
      },
      onSave: handleSave,
      onLongPressSave: handleSave,
      onShare: handleShare,
      onProjects: showTemplatePicker,
      onNew: handleNewProject,
      onHelp: () => {
        setupParentGuide()
        parentGuide?.open()
      },
    })
    renderSaveStatus = header.renderSaveStatus
    mountModeBody(bodyHost, {
      activeMode,
      initialProject: activeMode === 'build' || activeMode === 'share' ? currentProject : null,
      onWorkspaceReady: (ws, st) => {
        workspace = ws
        store = st
        setupAutoSave()
        // Hint card muncul setelah template dipilih.
        const tplId = currentProject?.templateId
        if (tplId) mountHintCardForTemplate(tplId)
        setupIdleHint()
        checkFirstRun().then((isFirstRun) => {
          if (isFirstRun) {
            // Fix Bug C: onboarding dipindah ke after template/project selected.
            showTemplatePicker()
          } else if (!currentProject) {
            restoreLastProject().then((proj) => {
              if (proj) {
                currentProject = proj
                store.loadFromProject(proj)
              }
              // Returning user dengan sessionCount < 3: tawarkan tour.
              getSetting('onboardingSessionCount').then((count) => {
                if ((count ?? 0) < 3) startOnboardingTour()
              })
            })
          } else {
            getSetting('onboardingSessionCount').then((count) => {
              if ((count ?? 0) < 3) startOnboardingTour()
            })
          }
        })
      },
      onEdit: () => {
        activeMode = 'build'
        render()
      },
    })
  }

  async function handlePlayRequested() {
    if (!workspace) {
      console.warn('[App] No workspace, cannot play')
      return
    }
    try {
      // Fix Bug A: auto-switch ke PLAY mode supaya iframe pindah ke stage visible.
      if (activeMode !== 'play') {
        activeMode = 'play'
        render()
      }

      // Tunggu PlayMode mount + mountSandboxIframe(stage) selesai pindahkan iframe.
      await new Promise((resolve) => setTimeout(resolve, 80))

      // Fix Bug A: pastikan iframe benar-benar berada di stage visible.
      const stage = bodyHost.querySelector('[data-role="stage"]')
      if (stage) mountSandboxIframe(stage)

      const initialSprites = store?.getInitialSprites?.() || []
      const userCode = generateProjectCode(workspace, { initialSprites })
      await initSandbox(userCode, initialSprites)
      trackEvent('play_started')
    } catch (err) {
      console.error('[App] Play failed:', err)
    }
  }

  bodyHost.addEventListener('play_requested', handlePlayRequested)

  trackEvent('studio_opened', {
    templateId: currentProject?.templateId || 'none',
    isReturningUser: !!currentProject,
  })
  render()
}
